import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBan, FaCheck, FaChevronDown, FaEye, FaEyeSlash, FaPlay, FaSearch, FaSpinner,
  FaSyncAlt, FaTimes, FaTrash, FaVideo,
} from 'react-icons/fa';
import { fetchStudioCourses } from '../../../services/learnService';
import { deleteTeacherCourse, moderateCourse } from '../../../services/courseService';
import usePagination from '../../../hooks/usePagination';
import { AdminBadge } from './AdminListCard';
import AdminEmptyState from './AdminEmptyState';
import Pagination from '../../../components/common/Pagination';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

const PAGE_SIZE = 6;

const CourseStatusBadges = ({ c }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {c.status === 'approved' && <AdminBadge variant="success">Approved</AdminBadge>}
    {c.status === 'pending' && <AdminBadge variant="warning">Pending review</AdminBadge>}
    {c.status === 'rejected' && <AdminBadge variant="danger">Rejected</AdminBadge>}
    {c.is_hidden && <AdminBadge variant="default">Hidden</AdminBadge>}
  </div>
);

/** Read-only course card with inline details + moderation. Content editing stays with teachers. */
const CourseCard = ({ c, expanded, onToggle, busy, onModerate, onDelete }) => {
  const pct = c.lessons ? Math.round((c.lessons_with_video / c.lessons) * 100) : 0;
  const managed = c.enrollment_mode === 'managed';
  return (
    <article
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Hide' : 'View'} details for ${c.title}`}
      className="bg-white rounded-xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-designer-primary/30 transition-all p-5 flex flex-col gap-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary"
    >
      <div className="min-w-0">
        <div className="mb-2"><CourseStatusBadges c={c} /></div>
        <h3 className="font-semibold text-designer-dark leading-snug break-words">{c.title}</h3>
        <p className="text-xs text-gray-500 mt-1 break-words">by {c.teacher_name} · <span className="font-mono">/{c.slug}</span></p>
      </div>
      <div>
        <div className="flex items-baseline justify-between gap-3 text-xs text-gray-600">
          <span className="tabular-nums">{c.lessons_with_video}/{c.lessons} classes with video</span>
          <span className="font-bold text-designer-primary tabular-nums">{pct}%</span>
        </div>
        <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${c.title} video readiness`}>
          <div className="h-full bg-gradient-to-r from-designer-primary to-red-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gray-100">{c.resources} files</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gray-100">{c.students} students</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gray-100">{managed ? 'Paid course' : 'Open enrollment'}</span>
      </div>

      {expanded && (
        <div className="space-y-3 rounded-xl bg-gray-50/70 border border-gray-100 p-3.5" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            {[
              { v: c.lessons, l: 'Classes' },
              { v: `${pct}%`, l: 'With video' },
              { v: c.resources, l: 'Files' },
              { v: c.students, l: 'Students' },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-white border border-gray-100 px-2 py-2.5">
                <p className="font-bold text-designer-dark tabular-nums text-sm">{s.v}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{s.l}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Moderation</p>
          <div className="grid grid-cols-2 gap-2">
            {c.status !== 'approved' && (
              <button
                type="button"
                disabled={!!busy}
                onClick={() => onModerate(c, { status: 'approved' })}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
              >
                <FaCheck /> Approve
              </button>
            )}
            {c.status !== 'rejected' && (
              <button
                type="button"
                disabled={!!busy}
                onClick={() => onModerate(c, { status: 'rejected' })}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-amber-300 text-amber-700 hover:bg-amber-50 text-xs font-semibold transition-colors disabled:opacity-50"
              >
                <FaBan /> Reject
              </button>
            )}
            <button
              type="button"
              disabled={!!busy}
              onClick={() => onModerate(c, { is_hidden: !c.is_hidden })}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:border-designer-primary hover:text-designer-primary text-xs font-semibold transition-colors disabled:opacity-50"
            >
              {c.is_hidden ? <FaEye /> : <FaEyeSlash />} {c.is_hidden ? 'Unhide' : 'Hide'}
            </button>
            <button
              type="button"
              disabled={!!busy || managed}
              title={managed ? 'Paid courses cannot be deleted. Hide them instead.' : 'Delete this course'}
              onClick={() => onDelete(c)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold transition-colors disabled:opacity-50"
            >
              <FaTrash /> Delete
            </button>
          </div>
          <a
            href={`/learn/${c.slug}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:border-designer-primary hover:text-designer-primary transition-colors"
          >
            <FaPlay className="w-3 h-3" /> Preview in player
          </a>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 mt-auto">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400">
          {expanded ? 'Hide details' : 'View details'}
          <FaChevronDown className={`w-3 h-3 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
        </span>
        <a
          href={`/learn/${c.slug}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-designer-primary hover:underline"
        >
          <FaPlay className="w-3 h-3" /> Watch
        </a>
      </div>
    </article>
  );
};

const AdminCourses = ({ getHeaders }) => {
  const [courses, setCourses] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [moderateBusy, setModerateBusy] = useState(false);

  const load = useCallback(() => {
    setError('');
    return fetchStudioCourses(getHeaders())
      .then(setCourses)
      .catch((err) => setError(err.message));
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    load().finally(() => setRefreshing(false));
  };

  const q = query.trim().toLowerCase();
  const filteredCourses = useMemo(() => (
    (courses || []).filter((c) => !q || `${c.title} ${c.teacher_name}`.toLowerCase().includes(q))
  ), [courses, q]);
  const pager = usePagination(filteredCourses, PAGE_SIZE, { resetKey: q });
  const pendingCount = (courses || []).filter((c) => c.status === 'pending').length;

  // Destructive moderation (reject / delete) asks for confirmation first.
  const [pendingAction, setPendingAction] = useState(null);

  const requestModerate = (course, patch) => {
    if (patch.status === 'rejected') return setPendingAction({ course, kind: 'reject', patch });
    return moderate(course, patch);
  };

  const requestDelete = (course) => setPendingAction({ course, kind: 'delete' });

  const moderate = async (course, patch) => {
    setModerateBusy(true);
    try {
      await moderateCourse(course.id, patch, getHeaders());
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setModerateBusy(false);
    }
  };

  const removeCourse = async (course) => {
    setModerateBusy(true);
    try {
      await deleteTeacherCourse(course.id, getHeaders());
      setExpandedId(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setModerateBusy(false);
    }
  };

  const confirmPending = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) return;
    if (action.kind === 'delete') return removeCourse(action.course);
    return moderate(action.course, action.patch);
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!courses) return <p className="text-sm text-gray-500"><FaSpinner className="inline animate-spin mr-2" />Loading courses…</p>;

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses or teachers…"
            aria-label="Search courses"
            className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-designer-primary outline-none text-sm shadow-sm"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              <FaTimes className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:text-designer-primary hover:border-designer-primary/40 transition-colors disabled:opacity-50 shadow-sm shrink-0"
        >
          <FaSyncAlt className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {q ? (
        <p className="-mt-3 text-xs text-gray-400" aria-live="polite">
          {filteredCourses.length} course{filteredCourses.length === 1 ? '' : 's'} match &ldquo;{query.trim()}&rdquo;
        </p>
      ) : (
        pendingCount > 0 && (
          <p className="-mt-3 text-xs text-amber-600 font-medium" aria-live="polite">
            {pendingCount} course{pendingCount === 1 ? '' : 's'} waiting for review
          </p>
        )
      )}

      {filteredCourses.length ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3" ref={pager.listRef}>
            {pager.items.map((c) => (
              <CourseCard
                key={c.id}
                c={c}
                expanded={expandedId === c.id}
                onToggle={() => toggle(c.id)}
                busy={moderateBusy}
                onModerate={requestModerate}
                onDelete={requestDelete}
              />
            ))}
          </div>
          <Pagination currentPage={pager.page} totalPages={pager.totalPages} onPageChange={pager.changePage} />
        </>
      ) : (
        <AdminEmptyState
          icon={FaVideo}
          title={courses.length === 0 ? 'No courses yet' : 'No matching courses'}
          description={courses.length === 0 ? 'Courses created by teachers will appear here.' : 'Try a different search.'}
        />
      )}

      <ConfirmDialog
        isOpen={!!pendingAction}
        title={pendingAction?.kind === 'delete' ? 'Delete course?' : 'Reject course?'}
        message={
          pendingAction?.kind === 'delete'
            ? `Delete "${pendingAction?.course.title}" permanently? Enrollments for it are removed too.`
            : `Reject "${pendingAction?.course.title}"? It will be hidden from students and teachers.`
        }
        confirmText={pendingAction?.kind === 'delete' ? 'Delete' : 'Reject'}
        type={pendingAction?.kind === 'delete' ? 'danger' : 'warning'}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmPending}
      />
    </div>
  );
};

export default AdminCourses;
